import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import BaseChart from '../../../Charts/BaseChart';
import type { NebulaModelUsageItem } from '../../../../types/nebula-insights';
import { DASHBOARD_PIE_COLORS, dashboardPieTooltip } from '../chartTheme';

interface ModelUsageChartProps {
  data: NebulaModelUsageItem[];
  loading?: boolean;
}

const ModelUsageChart: React.FC<ModelUsageChartProps> = ({ data, loading = false }) => {
  const option = useMemo<EChartsOption>(() => ({
    backgroundColor: '#ffffff',
    tooltip: {
      ...dashboardPieTooltip,
      formatter: (params: any) =>
        `${params.name}: ${params.value} (${params.percent}%)`,
    },
    series: [
      {
        type: 'pie',
        radius: ['52%', '72%'],
        center: ['50%', '46%'],
        avoidLabelOverlap: true,
        label: { show: false },
        itemStyle: {
          borderColor: '#ffffff',
          borderWidth: 2,
        },
        data: data.map((item, index) => ({
          name: item.model,
          value: item.count,
          itemStyle: { color: DASHBOARD_PIE_COLORS[index % DASHBOARD_PIE_COLORS.length] },
        })),
      },
    ],
  }), [data]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 h-full">
      <div className="mb-4">
        <h3 className="text-sm font-medium text-gray-900 font-family-messina-sans">
          Model usage
        </h3>
        <p className="text-xs text-gray-500 font-family-messina-sans mt-1">
          Assistant responses by model
        </p>
      </div>
      <div className="h-[180px]">
        {data.length === 0 && !loading ? (
          <div className="h-full flex items-center justify-center text-sm text-gray-500 font-family-messina-sans">
            No model data in this period
          </div>
        ) : (
          <BaseChart option={option} loading={loading} style={{ height: '180px' }} />
        )}
      </div>
      {data.length > 0 && (
        <ul className="mt-4 space-y-2">
          {data.map((item, index) => (
            <li
              key={item.model}
              className="flex items-center justify-between text-xs font-family-messina-sans"
            >
              <span className="flex items-center gap-2 text-gray-600">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: DASHBOARD_PIE_COLORS[index % DASHBOARD_PIE_COLORS.length] }}
                />
                {item.model}
              </span>
              <span className="text-gray-900 font-medium">{item.percentage}%</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ModelUsageChart;
